import { parse, parseISO, isValid, format, differenceInYears } from 'date-fns';
import { formatInTimeZone, toDate } from 'date-fns-tz';

export const parseDate = (dateStr) => {
  if (!dateStr) return null;
  
  try {
    // If it's already a Date object, return it
    if (dateStr instanceof Date) {
      return isValid(dateStr) ? dateStr : null;
    }
    
    // Ensure we're working with a string
    if (typeof dateStr !== 'string') {
      return null;
    }

    // Try parsing as MM/DD/YYYY
    if (dateStr.includes('/')) {
      const parsedDate = parse(dateStr, 'MM/dd/yyyy', new Date());
      if (isValid(parsedDate)) {
        return parsedDate;
      }
    }
    
    // Try parsing as ISO format
    if (dateStr.includes('-')) {
      const isoDate = parseISO(dateStr);
      if (isValid(isoDate)) {
        return isoDate;
      }
    }
    
    return null;
  } catch (error) {
    console.error('Error parsing date:', error);
    return null;
  }
};

export const calculateAge = (dateOfBirth, session, competition) => {
  if (!dateOfBirth) return null;

  try {
    const birthDate = parseDate(dateOfBirth);
    if (!birthDate) return null;

    // First try to use session start date
    if (session?.startDate) {
      const sessionDate = parseDate(session.startDate);
      if (sessionDate) {
        return differenceInYears(sessionDate, birthDate);
      }
    }

    // Fall back to competition start date
    if (competition?.startDate) {
      const competitionDate = parseDate(competition.startDate);
      if (competitionDate) {
        return differenceInYears(competitionDate, birthDate);
      }
    }

    // Fall back to today's date if no competition/session date available
    return differenceInYears(new Date(), birthDate);
  } catch (error) {
    console.error('Error calculating age:', error);
    return null;
  }
};

/**
 * Calculate program age (USAG style): competition year - birth year
 * Returns stored programAgeGroupAge if present, otherwise calculates from dateOfBirth
 */
export const calculateProgramAge = (athlete, competition) => {
  // Return stored value if present and valid
  if (athlete?.programAgeGroupAge != null) {
    const stored = String(athlete.programAgeGroupAge).toLowerCase();
    if (stored !== 'nan' && stored !== '') {
      return athlete.programAgeGroupAge;
    }
  }

  // Calculate from dateOfBirth if available
  if (!athlete?.dateOfBirth) return null;

  try {
    const birthDate = parseDate(athlete.dateOfBirth);
    if (!birthDate) return null;

    const birthYear = birthDate.getFullYear();

    // Use competition start date year
    if (competition?.startDate) {
      const competitionDate = parseDate(competition.startDate);
      if (competitionDate) {
        return competitionDate.getFullYear() - birthYear;
      }
    }

    // Fall back to current year
    return new Date().getFullYear() - birthYear;
  } catch (error) {
    console.error('Error calculating program age:', error);
    return null;
  }
};

export const formatDate = (dateStr, formatStr) => {
  if (!dateStr) return '';
  
  const parsedDate = parseDate(dateStr);
  if (!parsedDate) return dateStr;
  
  try {
    return format(parsedDate, formatStr);
  } catch (error) {
    console.error('Error formatting date:', error);
    return dateStr;
  }
};

export const parseTime = (timeStr) => {
  if (!timeStr) return null;

  try {
    // If it's already a Date object, return it
    if (timeStr instanceof Date) {
      return isValid(timeStr) ? timeStr : null;
    }

    // Ensure we're working with a string
    if (typeof timeStr !== 'string') {
      return null;
    }

    // Handle 12-hour format like "8:00 AM" or "8:00 PM"
    if (timeStr.includes('AM') || timeStr.includes('PM')) {
      const match = timeStr.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
      if (match) {
        let [, hours, minutes, period] = match;
        hours = parseInt(hours);
        minutes = parseInt(minutes);

        // Convert to 24-hour format
        if (period.toUpperCase() === 'PM' && hours !== 12) {
          hours += 12;
        } else if (period.toUpperCase() === 'AM' && hours === 12) {
          hours = 0;
        }

        // Create a date object with today's date and the parsed time
        const today = new Date();
        today.setHours(hours, minutes, 0, 0);
        return today;
      }
    }

    // Handle MM/DD/YYYY HH:MM:SS format (as found in existing stored data)
    if (timeStr.includes('/') && timeStr.includes(':') && timeStr.includes(' ')) {
      try {
        // Parse MM/DD/YYYY HH:MM:SS format (e.g., "01/23/2026 08:50:00")
        const [datePart, timePart] = timeStr.split(' ');
        const [month, day, year] = datePart.split('/');
        const [hours, minutes, seconds] = timePart.split(':');
        
        // Create date object manually
        const date = new Date(year, month - 1, day, hours, minutes, seconds || 0);
        if (isValid(date)) {
          return date;
        }
      } catch (e) {
        console.error('Error parsing MM/DD/YYYY HH:MM:SS format:', e);
      }
    }

    // Try parsing as full ISO datetime format (with or without 'T')
    if (timeStr.includes('-') && timeStr.includes(':')) {
      const normalized = timeStr.replace(' ', 'T');
      const isoDate = parseISO(normalized);
      if (isValid(isoDate)) {
        return isoDate;
      }
    }

    return null;
  } catch (error) {
    console.error('Error parsing time:', error);
    return null;
  }
};

/**
 * Parse a time string in the context of a specific timezone
 * @param {string} timeStr - ISO datetime string like "2026-01-23 08:50:00" or "2026-01-23T08:50:00"
 * @param {string} timezone - IANA timezone identifier like "America/New_York"
 * @returns {Date|null} - Date object or null if parsing fails
 */
export const parseTimeInTimezone = (timeStr, timezone) => {
  if (!timeStr) return null;

  try {
    // Normalize the time string to ISO format with 'T'
    const normalized = timeStr.replace(' ', 'T');

    // Parse as if it's in the specified timezone
    // Note: parseISO treats the string as local time, but we need to interpret it in the competition's timezone
    const date = toDate(normalized, { timeZone: timezone });

    return isValid(date) ? date : null;
  } catch (error) {
    console.error('Error parsing time in timezone:', error);
    return null;
  }
};

/**
 * Format a date/time in a specific timezone
 * @param {Date|string} dateStr - Date object or ISO string
 * @param {string} formatStr - Format string for date-fns
 * @param {string} timezone - IANA timezone identifier
 * @returns {string} - Formatted time string
 */
export const formatInTimezone = (dateStr, formatStr, timezone) => {
  if (!dateStr) return '';

  try {
    const date = typeof dateStr === 'string' ? parseDate(dateStr) : dateStr;
    if (!date || !isValid(date)) return dateStr;

    return formatInTimeZone(date, timezone || 'America/New_York', formatStr);
  } catch (error) {
    return dateStr;
  }
};

/**
 * Shift all dates in a competition (including sessions and timeBlocks) to align with new start date
 * This ensures dates "fall in line" even when original dates were corrupted
 * @param {Object} competitionWrapper - The full competition object with sessions
 * @param {string} newStartDateStr - The new start date string (YYYY-MM-DD)
 * @returns {Object} - The updated competition object
 */
export const shiftCompetitionDates = (competitionWrapper, newStartDateStr) => {
  if (!competitionWrapper?.competition || !newStartDateStr) {
    return competitionWrapper;
  }

  try {
    // Create local date objects
    const createLocalDate = (dateStr) => {
        if (!dateStr) return null;
        // Handle both T and space as separators, or no separator
        const cleanStr = String(dateStr).split(/[T ]/)[0];
        const [y, m, d] = cleanStr.split('-').map(Number);
        return new Date(y, m - 1, d);
    };

    const newStart = createLocalDate(newStartDateStr);

    if (!isValid(newStart)) {
      return competitionWrapper;
    }

    // Deep clone to avoid mutation
    const updated = JSON.parse(JSON.stringify(competitionWrapper));

    // Calculate competition duration from old dates
    const oldStart = createLocalDate(updated.competition.startDate);
    const oldEnd = createLocalDate(updated.competition.endDate);
    let competitionDurationDays = 1; // Default 1 day
    if (oldStart && oldEnd && isValid(oldStart) && isValid(oldEnd)) {
      competitionDurationDays = Math.max(1, Math.round((oldEnd.getTime() - oldStart.getTime()) / (1000 * 60 * 60 * 24)));
    }

    // Set new competition dates
    updated.competition.startDate = newStartDateStr;
    const newEnd = new Date(newStart.getTime());
    newEnd.setDate(newEnd.getDate() + competitionDurationDays); // Duration includes the end day
    updated.competition.endDate = format(newEnd, 'yyyy-MM-dd');

    // Find session 1 (lowest sessionNumber) to use as reference
    let session1 = null;
    if (Array.isArray(updated.sessions)) {
      // Sort sessions by sessionNumber to find the first one
      const sortedSessions = [...updated.sessions].sort((a, b) => (a.sessionNumber || 0) - (b.sessionNumber || 0));
      session1 = sortedSessions.find(s => s.startDate);
    }

    // If no session 1 found, no shift needed
    if (!session1 || !session1.startDate) {
      // console.log(`[shiftCompetitionDates] No session 1 found, only updating competition dates`);
      return updated;
    }

    // Calculate shift needed to make session 1 start on the new competition start date
    const session1Date = parseDate(session1.startDate);
    if (!session1Date || !isValid(session1Date)) {
      // console.log(`[shiftCompetitionDates] Invalid session 1 date, only updating competition dates`);
      return updated;
    }

    const shiftDays = Math.round((newStart.getTime() - session1Date.getTime()) / (1000 * 60 * 60 * 24));

    // console.log(`[shiftCompetitionDates] Old competition start: ${oldStart?.toISOString()}`);
    // console.log(`[shiftCompetitionDates] Competition duration: ${competitionDurationDays} days`);
    // console.log(`[shiftCompetitionDates] New start: ${newStart.toISOString()}`);
    // console.log(`[shiftCompetitionDates] Session 1 date: ${session1Date.toISOString()}`);
    // console.log(`[shiftCompetitionDates] Shifting all dates by ${shiftDays} days so session 1 aligns with new start`);

    if (shiftDays === 0) return updated;

    // Helper to shift any date by the calculated number of days
    const shiftDate = (currentDateStr, isDateTime = false) => {
      if (!currentDateStr) return currentDateStr;

      let currentDate;
      if (isDateTime || currentDateStr.includes(' ') || currentDateStr.includes('T')) {
          currentDate = parseTime(currentDateStr);
      } else {
          currentDate = parseDate(currentDateStr);
      }

      if (!isValid(currentDate)) {
        console.warn(`[shiftCompetitionDates] Invalid current date: ${currentDateStr}`);
        return currentDateStr;
      }

      // Shift by the calculated number of days
      const shiftedDate = new Date(currentDate.getTime());
      shiftedDate.setDate(shiftedDate.getDate() + shiftDays);

      // Format preserving original style
      if (isDateTime || currentDateStr.includes(':') || currentDateStr.includes('T')) {
        if (currentDateStr.includes('T')) {
          return format(shiftedDate, "yyyy-MM-dd'T'HH:mm:ss"); // ISO-ish
        } else {
           return format(shiftedDate, "yyyy-MM-dd HH:mm:ss"); // SQL-ish
        }
      } else {
        return format(shiftedDate, 'yyyy-MM-dd');
      }
    };

    // 1. Set Competition Dates (not shifted, but recalculated)
    updated.competition.startDate = newStartDateStr; // Explicitly set new start
    // endDate already set above based on duration

    // Shift registration deadline if it exists
    if (updated.competition.registrationDeadline) {
      updated.competition.registrationDeadline = shiftDate(updated.competition.registrationDeadline, true);
    }

    // Shift Age Group Dates if they exist
    if (Array.isArray(updated.ageGroups)) {
      updated.ageGroups = updated.ageGroups.map(group => {
        const updatedGroup = { ...group };
        if (updatedGroup.startDate) {
          updatedGroup.startDate = shiftDate(updatedGroup.startDate, true);
        }
        if (updatedGroup.endDate) {
          updatedGroup.endDate = shiftDate(updatedGroup.endDate, true);
        }
        if (updatedGroup.Start_Date) {
          updatedGroup.Start_Date = shiftDate(updatedGroup.Start_Date, true);
        }
        if (updatedGroup.End_Date) {
          updatedGroup.End_Date = shiftDate(updatedGroup.End_Date, true);
        }
        return updatedGroup;
      });
    }

    // 2. Shift Session Dates
    if (Array.isArray(updated.sessions)) {
      updated.sessions = updated.sessions.map(session => {
        const updatedSession = { ...session };

        if (updatedSession.startDate) {
          updatedSession.startDate = shiftDate(updatedSession.startDate, false);
        }
        if (updatedSession.endDate) {
          updatedSession.endDate = shiftDate(updatedSession.endDate, false);
        }

        // 3. Shift TimeBlocks
        if (updatedSession.timeBlocks) {
          const newTimeBlocks = {};
          Object.entries(updatedSession.timeBlocks).forEach(([key, value]) => {
             newTimeBlocks[key] = shiftDate(value, true);
          });
          updatedSession.timeBlocks = newTimeBlocks;
        }

        return updatedSession;
      });
    }

    return updated;
  } catch (error) {
    console.error('Error shifting competition dates:', error);
    return competitionWrapper;
  }
};
